<template>
	<div id="app">
		<RiskNotice ref="riskNoticePopup" />
		<StatusPopup ref="globalToast" />
		<router-view v-slot="{ Component }">
			<transition name="van-fade" mode="out-in">
				<component :is="Component" />
			</transition>
		</router-view>
	</div>
</template>

<script>
import {
	getHomeURL,
	getChatMessages,
	getChatMessageOfflineURL,
	BASE_COLOR,
	getSysConfigURL,
	getMarkChatDelivered,
	getDeletedMsgs,
	getChatUrlRes,
	getUnreadRiskNoticeUrl
} from './common/js/constants.js'
import defaultHomeOrder from "./common/js/homeOrder.js"
import defaultUcenterOrder from "./common/js/ucenterOrder.js"
import RiskNotice from "./pages/RiskNotice/RiskNotice.vue"
import StatusPopup from "./components/StatusPopup.vue"
import { useAppStore } from "./store/index.js"
import {
	ROUTE_CHAT,
	RISK_NOTICE_POPUP_PAGE
} from "./common/js/routes"
import eventBus from './common/js/eventBus.js'
import { getStorageSync, setStorageSync, getBooleanStorageSync } from './utils/storage.js'
import { dedupeMessages } from './utils/chatUtils.js'
import { showToast } from 'vant'
import { version } from '../package.json'
import { registerGlobalToast } from './utils/toastService.js'

export default {
	name: 'App',
	components: {
		RiskNotice,
		StatusPopup
	},
	data() {
		return {
			msgList: [],
			chatList: [],
			unreadCounts: {},
			chatUrlCount: 0,
			unreadRiskInterval: null,
			chatMessageInterval: null,
			offlineMessageInterval: null,
			chatMessageFailCount: 0,
			offlineMessageFailCount: 0,
			version: version,
		}
	},
	computed: {
		store() {
			return useAppStore()
		}
	},
	mounted() {
		registerGlobalToast(this.$refs.globalToast)

		// App launch logic
		setTimeout(() => {
			setStorageSync('promptAppDownload', 'true')
			this.getEncrypterPublicKey()
			this.$setDefaultAddress()
		}, 1500)
		setStorageSync("updateNoticeShouldDisplay", "true")

		// Initialize cache data
		if (!getStorageSync('homeDataCache')) {
			setStorageSync('homeDataCache', JSON.stringify(defaultHomeOrder.data))
		}

		// ucenter cache store data issue fix
		setStorageSync("ucenterDataCache", "")
		if (!getStorageSync('ucenterDataCache')) {
			let defaultUcenterOrderData = []
			if (this.isVersionGreater('2.2.24')) {
				defaultUcenterOrderData.push(defaultUcenterOrder.data[1])
				setStorageSync('ucenterDataCache', JSON.stringify(defaultUcenterOrderData))
			} else {
				defaultUcenterOrderData.push(defaultUcenterOrder.data[0])
				setStorageSync('ucenterDataCache', JSON.stringify(defaultUcenterOrderData))
			}
		}

		// Handle refcode from URL
		const currentUrl = window.location.href
		if (currentUrl.includes('?code=')) {
			const refcode = currentUrl.split("?code=")[1]
			window.sessionStorage.setItem("refCode", refcode)
		}

		// For risk notice popup
		this.getUnreadRiskNotices()
		this.getRiskNoticeSound()

		eventBus.$on('chatBootstrap', () => {
			this.pollChatMessagesOnce()
		})

		setTimeout(() => {
			this.getTheme()
			this.chatUrlCount = 0
			this.getOwnChatUrl()
			this.getChatMessage()
			this.getOfflineMessages()

			if (getStorageSync('token') && getBooleanStorageSync('chatUpdated')) {
				setTimeout(() => {
					this.pollChatMessagesOnce()
				}, 500)
			}
		}, 1500)

		// Initialize chat data
		if (!getStorageSync("chatUpdated") || getStorageSync("chatUpdated") === '') {
			setStorageSync("chatUpdated", "false")
		}
		if (!getStorageSync("systemMessages") || getStorageSync("systemMessages") === '') {
			setStorageSync("systemMessages", "[]")
		}
		if (!getStorageSync("systemUnread") || getStorageSync("systemUnread") === '') {
			setStorageSync("systemUnread", "0")
		}
	},
	beforeUnmount() {
		eventBus.$off('chatBootstrap')
		if (this.unreadRiskInterval) {
			clearInterval(this.unreadRiskInterval)
		}
		if (this.chatMessageInterval) {
			clearInterval(this.chatMessageInterval)
		}
		if (this.offlineMessageInterval) {
			clearInterval(this.offlineMessageInterval)
		}
	},
	methods: {
		isVersionGreater(compareVersion) {
			const currentParts = version
			const compareParts = compareVersion
			if (currentParts > compareParts) {
				return true
			}
			return false
		},
		getEncrypterPublicKey() {
			let context = this
			context.http({
				url: getHomeURL(),
				method: 'post',
				data: {},
				context
			}, (errorMsg) => {
				showToast({
					message: this.$t(errorMsg),
					type: 'fail',
				})
			}).then(res => {
				this.store.setPublicKey(res['data']['encript_public_key'])
			})
		},
		getOfflineMessages() {
			this.offlineMessageInterval = setInterval(() => {
				if (getStorageSync('token') && getBooleanStorageSync('chatUpdated')) {
					let context = this
					let orderId = getStorageSync('last_msg_id') || ''
					context.http({
						url: getChatMessageOfflineURL(),
						method: 'post',
						data: {
							receiver: getStorageSync('last_msg_receiver'),
							user_id: getStorageSync('uid'),
							order_id: orderId
						},
						chatURL: true,
					}, (errorMsg) => {
						// Increment failure count
						context.offlineMessageFailCount++
						console.error(`Offline message API failed (${context.offlineMessageFailCount}/3):`, errorMsg)
						
						// Stop interval after 3 consecutive failures
						if (context.offlineMessageFailCount >= 3) {
							console.error('Offline message API failed 3 times consecutively. Stopping interval.')
							clearInterval(context.offlineMessageInterval)
							context.offlineMessageInterval = null
						}
					}).then(res => {
						if (res && res.status === 200) {
							// Reset failure count on success
							context.offlineMessageFailCount = 0
							const responseData = res.data || {}

							context.handleSystemMessage(responseData.systemMessages)

							if (responseData.msgList && responseData.msgList.length > 0) {
								let totalUnread = 0
								context.unreadCounts = responseData.count
								context.msgList = responseData.msgList
								context.msgList.forEach(element => {
									context.setAllMessagesList(element, true)
								})
								let agentList = JSON.parse(getStorageSync('agent_list') || '[]')
								agentList.forEach(element => {
									if (!(this.$route.name === 'Chat' && String(this.store.chatAgentId) == String(element.number))) {
										totalUnread += element.count
									}
								})
								eventBus.$emit('totalChanged', totalUnread)
								const msgListReversed = context.msgList.reverse()
								setStorageSync('last_msg_id', msgListReversed[0].order_id)
								setStorageSync('last_msg_receiver', msgListReversed[0].receiver)
							}
						}
					})
				}
			}, 60000)
		},
		getChatMessage() {
			this.chatMessageInterval = setInterval(() => {
				this.pollChatMessagesOnce()
			}, 3000)
		},
		pollChatMessagesOnce() {
			if (!getStorageSync('token') || !getBooleanStorageSync('chatUpdated')) {
				return Promise.resolve()
			}

			const context = this
			const orderId = getStorageSync('last_msg_id') || ''

			return context.http({
				url: getChatMessages(),
				method: 'post',
				data: {
					user_id: getStorageSync('uid'),
					receiver: getStorageSync('last_msg_receiver'),
					order_id: orderId
				},
				chatURL: true,
			}, (errorMsg) => {
				context.chatMessageFailCount++
				console.error(`Chat message API failed (${context.chatMessageFailCount}/3):`, errorMsg)

				if (context.chatMessageFailCount >= 3) {
					console.error('Chat message API failed 3 times consecutively. Stopping interval.')
					clearInterval(context.chatMessageInterval)
					context.chatMessageInterval = null
				}
			}).then(res => {
				if (res && res.status === 200) {
					context.chatMessageFailCount = 0
					const responseData = res.data || {}

					context.handleSystemMessage(responseData.systemMessages)

					if (responseData.msgList && responseData.msgList.length > 0) {
						let totalUnread = 0
						context.unreadCounts = responseData.count
						context.msgList = responseData.msgList
						context.msgList.forEach(element => {
							context.setAllMessagesList(element, true)
						})
						if (context.msgList.length > 0) {
							eventBus.$emit("onReceivedForChatlist", "USER")
						}

						let agentList = JSON.parse(getStorageSync('agent_list') || '[]')
						agentList.forEach(element => {
							if (!(context.$route.name === 'Chat' && String(context.store.chatAgentId) == String(element.number))) {
								totalUnread += element.count
							}
						})
						eventBus.$emit('totalChanged', totalUnread)

						const msgListReversed = context.msgList.reverse()
						setStorageSync('last_msg_id', msgListReversed[0].order_id)
						setStorageSync('last_msg_receiver', msgListReversed[0].receiver)

						context.markChatDelivered()
					}

					eventBus.$emit('updateTotalCount')
				}
			}).catch(() => {})
		},
		setAllMessagesList(messageItem, isEmit) {
			let agentList = JSON.parse(getStorageSync('agent_list') || '[]')

			if (messageItem.type === -100) {
				const deleted = this.store.deletedMsgs || []
				this.store.setDeletedMsgs(messageItem.message)
				eventBus.$emit("msgDeleted", messageItem.message)
				agentList.forEach((e) => {
					e.messages.forEach((el) => {
						if (el.order_id === messageItem.message) {
							el.type = -100
							if (e.unreadMsgIds && e.unreadMsgIds.length > 0) {
								if (e.unreadMsgIds.includes(el.order_id)) {
									if (e.count !== 0) {
										e.count = e.count - 1
									}
								}
							}
						}
					})
				})
				setStorageSync('agent_list', JSON.stringify(agentList))
			} else {
				if (agentList.length > 0) {
					let shouldEmit = false
					agentList.forEach(element => {
						const isMsgExist = element.messages.find(item => item.order_id == messageItem.order_id)

						if (!isMsgExist) {
							if (element.number == messageItem.receiver || element.number == messageItem.sender) {
								element.messages.push(messageItem)
								element.last_msg = messageItem
								const isActiveChat = this.$route.name === 'Chat'
									&& String(this.store.chatAgentId) == String(element.number)
								if (!isActiveChat) {
									element.count++
									if (element.unreadMsgIds) {
										element.unreadMsgIds.push(messageItem.order_id)
									} else {
										element.unreadMsgIds = []
										element.unreadMsgIds.push(messageItem.order_id)
									}
								}

								element.messages = dedupeMessages(element.messages)
								element.messages.sort((a, b) => a.time - b.time)
								setStorageSync('agent_list', JSON.stringify(agentList))

								if (isEmit) {
									shouldEmit = true
								}
							}
						}
					})
					if (shouldEmit && isEmit) {
						eventBus.$emit("onChatReceived", messageItem)
					}
				}
			}
		},
		handleSystemMessage(systemMessages) {
			const msgList = systemMessages && systemMessages.msgList ? systemMessages.msgList : []
			if (msgList.length > 0) {
				msgList.forEach((message) => {
					const availableMessages = JSON.parse(getStorageSync('systemMessages') || '[]')
					const messageAvailable = availableMessages.find(element => element.order_id === message.order_id)

					if (!messageAvailable) {
						message.readStatus = false
						availableMessages.push(message)
						setStorageSync('systemMessages', JSON.stringify(availableMessages))
						setStorageSync('last_msg_id', message.order_id)
						setStorageSync('last_msg_receiver', message.receiver)

						let availableCount = parseInt(getStorageSync('systemUnread') || '0')
						availableCount += 1

						if (this.store.chatAgentId !== 'SYSTEM') {
							setStorageSync('systemUnread', availableCount.toString())
						} else {
							setStorageSync('systemUnread', '0')
						}

						eventBus.$emit('updateTotalCount')
						eventBus.$emit("onChatReceived", message)
						eventBus.$emit("onReceivedForChatlist", "SYSTEM")
					}
				})
			}
		},
		getTheme() {
			this.$nextTick(() => {
				let context = this
				context.http({
					url: getSysConfigURL('name'),
					method: 'post',
					data: {
						name: 'theme_colour'
					},
					context
				}, (errorMsg) => {
					showToast({
						message: this.$t(errorMsg),
						type: 'fail',
					})
				}).then(res => {
					const color = res.data
					this.$themeBase = color
					document.querySelector('#app').style.setProperty('--baseRedColor', color)
				})
			})
		},
		getOwnChatUrl() {
			let context = this
			context.http({
				url: getSysConfigURL('name'),
				method: 'post',
				data: {
					name: 'new_chat_links'
				},
				context
			}, (errorMsg) => {
				showToast({
					message: this.$t(errorMsg),
					type: 'fail',
				})
			}).then(res => {
				const own_chat_link = res.data
				let chat_urls = []
				setStorageSync('chatWorkingLines', "[]")
				const separatedItems = own_chat_link.split(',')

				separatedItems.forEach(item => {
					const trimmedItem = item.trim()
					if (trimmedItem !== '') {
						chat_urls.push(trimmedItem)
					}
				})

				if (chat_urls.length > 0) {
					setStorageSync('ownChatUrl', chat_urls[0])
				}

				context.getFastestline(chat_urls)
			})
		},
		getFastestline(chat_urls) {
			let context = this
			let chatWorkingLines = []
			let countChatUrl = 0
			chat_urls.forEach(chat_url => {
				countChatUrl++
				setStorageSync('ownChatUrl', chat_url)
				let startTimeForChatUrl = new Date().getTime()
				context.http({
					url: getChatUrlRes(),
					method: 'get',
					data: {},
					chatURL: true,
				}).then(res => {
					if (res.status === 200) {
						let endTimeForChatUrl = new Date().getTime()
						let elapsedTimeForChat = endTimeForChatUrl - startTimeForChatUrl
						let item = {}
						item.time = elapsedTimeForChat
						item.url = chat_url
						chatWorkingLines.push(item)
						setStorageSync('chatWorkingLines', JSON.stringify(chatWorkingLines))
						if (countChatUrl === chat_urls.length) {
							const stored = JSON.parse(getStorageSync('chatWorkingLines') || '[]')
							if (stored.length > 0) {
								stored.sort((a, b) => (a.time > b.time) ? 1 : -1)
								setStorageSync('ownChatUrl', stored[0].url)
							}
						}
					}
				}).catch(() => {
					if (countChatUrl === chat_urls.length) {
						const stored = JSON.parse(getStorageSync('chatWorkingLines') || '[]')
						if (stored.length > 0) {
							stored.sort((a, b) => (a.time > b.time) ? 1 : -1)
							setStorageSync('ownChatUrl', stored[0].url)
						}
					}
				})
			})
		},
		markChatDelivered() {
			let that = this
			that.http({
				url: getMarkChatDelivered(),
				method: 'post',
				data: {},
				chatURL: true,
			}, (errorMsg) => {
				showToast({
					message: this.$t(errorMsg),
					type: 'fail',
				})
			})
		},
		getUnreadRiskNotices() {
			this.unreadRiskInterval = setInterval(() => {
				if (getStorageSync('token')) {
					let context = this
					context.http({
						url: getUnreadRiskNoticeUrl(),
						method: 'post',
						data: {},
						context
					}).then(res => {
						if (res.data) {
							setStorageSync("risk_msg_id", res.data.risk_notice_id)
							setStorageSync("risk_msg_content", res.data.risk_notice_detail)
							context.showNotice()
						} else if (getStorageSync("risk_msg_id")) {
							context.showNotice()
						}
					})
				}
			}, 3000)
		},
		showNotice() {
			if (getStorageSync('token') !== '') {
				this.$refs.riskNoticePopup?.openNoticeAsPopup()
			}
		},
		getRiskNoticeSound() {
			let context = this
			context.http({
				url: getSysConfigURL('name'),
				method: "post",
				data: {
					name: 'risk_notice_audio'
				},
			}, (errorMsg) => {
				showToast({
					message: this.$t(errorMsg),
					type: 'fail',
				})
			}).then((res) => {
				setStorageSync('riskNoticeSound', res.data)
				setStorageSync('riskNoticeSoundRun', '0')
			})
		},
	}
}
</script>

<style lang="scss">
@import '../public/chat/styles/animation.css';
@import '../public/chat/customicons.css';
@import '../public/emoji/css/emoji.css';
@import '../public/globalStyles/global.css';

html,
body {
	background-color: #f0f3f7;
	padding-bottom: constant(safe-area-inset-bottom);
	padding-bottom: env(safe-area-inset-bottom);
	padding-top: constant(safe-area-inset-top);
	padding-top: env(safe-area-inset-top);
	background-size: cover;
}
</style>

